import type { ColorConfig } from '@tempo-io/roadmap-dto';
import chroma from 'chroma-js';
import type { ItemV2 } from '@/common/api/types/items';
import { getItemFieldValue } from './fieldExtraction';

export type ColorPalette = {
  id: string;
  label: string;
  headerColors: string[];
  itemColors: string[];
};

export type PaletteSwatches = {
  headerColors: string[];
  itemColors: string[];
};

export const DEFAULT_BASE_COLOR = '#9664FE';
export const DEFAULT_PALETTE_ID = 'endless-summer';
export const DEFAULT_FALLBACK_COLOR = '#D4D4D7';

export const withHash = (color: string): string => (color.startsWith('#') ? color : `#${color}`);

/**
 * Predefined color palettes
 * First 12 palettes mirror the loop-2026 prototype (tempo-io/prototype-playground
 * src/components/roadmap-timeline/timeline-palettes.ts). The first 5 itemColors
 * match the prototype's swatches exactly; the remaining 7 extend the family at
 * similar saturation. headerColors reuse the same swatches.
 */
export const PREDEFINED_PALETTES: ColorPalette[] = [
  {
    id: 'citrus',
    label: 'Wild Harvest',
    headerColors: ['#7798b8', '#F26464', '#5B6470'],
    itemColors: [
      '#75a781',
      '#e0d46a',
      '#ea9765',
      '#dd5362',
      '#C689DD',
      '#A7A577',
      '#807DE0',
      '#9FD1EA',
      '#d0d5d8',
      '#DDC5B9',
      '#8A787F',
      '#7b7b7b',
    ],
  },
  {
    id: 'groovy',
    label: 'Vivid Bloom',
    headerColors: ['#3156A3', '#F2AE1C', '#8C94A1'],
    itemColors: [
      '#f11a13',
      '#26C0A4',
      '#009deb',
      '#f14ea3',
      '#f57336',
      '#2AC053',
      '#007dc0',
      '#B55DB3',
      '#f2d408',
      '#bebebe',
      '#939393',
      '#676767',
    ],
  },
  {
    id: 'pastel',
    label: 'Soft Pastel',
    headerColors: ['#d47b60', '#94c793', '#7fa2a3'],
    itemColors: [
      '#E4B4DA',
      '#add6d7',
      '#D7C3AD',
      '#e2e49e',
      '#BCACD7',
      '#bae0b9',
      '#9d9365',
      '#81a780',
      '#985367',
      '#5b9798',
      '#9D7F7B',
      '#C7C4D7',
    ],
  },
  {
    id: 'autumn',
    label: 'Autumn Foliage',
    headerColors: ['#132D63', '#252525', '#23140E'],
    itemColors: [
      '#051D54',
      '#0F57A0',
      '#9FC1DB',
      '#9C2D3B',
      '#EF7321',
      '#EAA581',
      '#F5D096',
      '#2A462E',
      '#809F54',
      '#ADB694',
      '#4C250B',
      '#5A554F',
    ],
  },
  {
    id: 'neon',
    label: 'Neon Lights',
    headerColors: ['#181818', '#293D3D', '#9F9F9F'],
    itemColors: [
      '#A8145C',
      '#E36DBA',
      '#FDAE3F',
      '#FFF04E',
      '#DEFFC8',
      '#20C619',
      '#4CECC6',
      '#5CFBF2',
      '#549BFF',
      '#3100B1',
      '#9300AD',
      '#D2ACD4',
    ],
  },
  {
    id: 'ocean',
    label: 'Open Ocean',
    headerColors: ['#2E4257', '#57B0CA', '#8DCEB4'],
    itemColors: [
      '#132D63',
      '#2330FF',
      '#43A7C2',
      '#E0C75C',
      '#FDFC86',
      '#80FEB9',
      '#5ABA78',
      '#386130',
      '#2E344C',
      '#5A4B7B',
      '#A8A1BB',
      '#D4C7D2',
    ],
  },
  {
    id: 'mountain-views',
    label: 'Misty Highland',
    headerColors: ['#34661b', '#616a75', '#6eb0be'],
    itemColors: [
      '#2c3133',
      '#6eb0be',
      '#34661b',
      '#659860',
      '#6bc17c',
      '#c3906c',
      '#f4e60c',
      '#2d9bf0',
      '#77c7ce',
      '#abb7c8',
      '#8e8d8b',
      '#616a75',
    ],
  },
  {
    id: 'aegean-seaside',
    label: 'Aegean Seaside',
    headerColors: ['#4d3342', '#345d8b', '#e2d4c7'],
    itemColors: [
      '#00329d',
      '#0062c7',
      '#00aaf1',
      '#345601',
      '#a61c63',
      '#d85663',
      '#fc54d0',
      '#814039',
      '#968460',
      '#ffbc01',
      '#d4b888',
      '#a69898',
    ],
  },
  {
    id: 'endless-summer',
    label: 'Juniper Mist',
    headerColors: ['#19405f', '#D08380', '#5f6245'],
    itemColors: [
      '#1b736a',
      '#61b959',
      '#bfd5b6',
      '#022da0',
      '#0189ef',
      '#a1c7e0',
      '#f08659',
      '#a8231e',
      '#e8675f',
      '#4c401f',
      '#cccbc6',
      '#aa9a86',
    ],
  },
  {
    id: 'desert-skies',
    label: 'Desert Skies',
    headerColors: ['#ff9d00', '#f24726', '#1f1d1d'],
    itemColors: [
      '#c80a0a',
      '#ce2097',
      '#fa6a95',
      '#fe835d',
      '#dfaa1f',
      '#21dd25',
      '#3e6568',
      '#0ca789',
      '#0c6ef4',
      '#84dffa',
      '#e0ceaa',
      '#67716b',
    ],
  },
  {
    id: 'nordic-harbour',
    label: 'Quiet Harbour',
    headerColors: ['#923838', '#29313b', '#292928'],
    itemColors: [
      '#7a4618',
      '#ae6d4e',
      '#e1971c',
      '#eeddaa',
      '#e17373',
      '#826cea',
      '#3211c5',
      '#4e9fe6',
      '#ddebf2',
      '#84ae8d',
      '#b0a6a1',
      '#4c545b',
    ],
  },
  {
    id: 'tempo-suite',
    label: 'Tempo Suite',
    headerColors: ['#1c1c1d', '#323135', '#d4d4d7'],
    itemColors: [
      '#f44e42',
      '#9564fe',
      '#ffcc25',
      '#01b88a',
      '#f9b6b0',
      '#69f6ff',
      '#a3a38b',
      '#ff8374',
      '#d4a1ff',
      '#fff544',
      '#0deac9',
      '#deddc9',
    ],
  },
  {
    id: 'toy-store',
    label: 'Carnival Ride',
    headerColors: ['#cad2c5', '#84a98c', '#52796f'],
    itemColors: [
      '#19589a',
      '#416ddd',
      '#29c4de',
      '#13c6be',
      '#7aa603',
      '#e3c126',
      '#f26800',
      '#c73129',
      '#c81c69',
      '#726ead',
      '#35326f',
      '#313133',
    ],
  },
  {
    id: 'electric-avenue',
    label: 'Electric Avenue',
    headerColors: ['#101211', '#21372c', '#a5a2a9'],
    itemColors: [
      '#5c5c64',
      '#182350',
      '#95a801',
      '#fedc37',
      '#eb0100',
      '#934244',
      '#607297',
      '#9dc2ef',
      '#293d23',
      '#b2835d',
      '#be8999',
      '#dfcd80',
    ],
  },
  {
    id: 'midnight-metropolis',
    label: 'Midnight Metropolis',
    headerColors: ['#27262b', '#7d6b57', '#8a8381'],
    itemColors: [
      '#17161b',
      '#414045',
      '#826965',
      '#0c62b2',
      '#17aa80',
      '#85b2a7',
      '#ccb611',
      '#f2c15f',
      '#e5511f',
      '#ae063d',
      '#b14cb9',
      '#f9a3d6',
    ],
  },
  {
    id: 'laurentian-foliage',
    label: 'Sunlit Orchard',
    headerColors: ['#446829', '#c83c67', '#ec8e08'],
    itemColors: [
      '#d97145',
      '#f9b301',
      '#f42b54',
      '#9c1b41',
      '#f8b091',
      '#ac9d83',
      '#2c405b',
      '#3d76c6',
      '#6fb0ff',
      '#C7E673',
      '#619A33',
      '#3a4a2e',
    ],
  },
];

function getDefaultPalette(): ColorPalette {
  const palette = PREDEFINED_PALETTES.find((p) => p.id === DEFAULT_PALETTE_ID);
  if (palette) {
    return palette;
  }

  const fallback = PREDEFINED_PALETTES[0];
  if (!fallback) {
    throw new Error('No default palette available');
  }

  return fallback;
}

/**
 * Generate monochromatic color shades from a base color
 * Matches Roadmunk's implementation using monochromatic palette generation
 * @param baseColor - Hex color string (e.g., '#9664FE')
 * @returns Array of hex color strings (10 shades)
 */
export function getBasePaletteColors(baseColor: string): string[] {
  try {
    const palette = chroma
      .scale([chroma(baseColor).darken(2), baseColor, chroma(baseColor).brighten(2)])
      .mode('lab')
      .colors(6);

    // Add inverse set to avoid sudden color jump (similar to Roadmunk)
    // This creates a smoother transition: [dark -> light -> dark]
    const inversePalette = palette.slice(1, palette.length - 1).reverse();
    const fullPalette = [...palette, ...inversePalette];

    return fullPalette;
  } catch {
    // Fallback to Tempo Suite if base color is invalid
    return getDefaultPalette().itemColors;
  }
}

/**
 * Get a predefined palette by ID
 * @param paletteId - ID of the palette (e.g., 'tempo-suite')
 * @returns ColorPalette object
 */
export function getStaticPalette(paletteId: string): ColorPalette {
  const palette = PREDEFINED_PALETTES.find((p) => p.id === paletteId);

  if (!palette) {
    // Fallback to Tempo Suite if palette not found
    return getDefaultPalette();
  }

  return palette;
}

function getDefaultSwatches(): PaletteSwatches {
  const defaultPalette = getDefaultPalette();

  return {
    headerColors: defaultPalette.headerColors.length
      ? defaultPalette.headerColors
      : defaultPalette.itemColors,
    itemColors: defaultPalette.itemColors,
  };
}

function resolvePaletteSwatches(palette: ColorPalette): PaletteSwatches {
  return {
    headerColors: palette.headerColors.length ? palette.headerColors : palette.itemColors,
    itemColors: palette.itemColors,
  };
}

/**
 * Get both header and item swatches based on colorConfig
 */
export function getPaletteSwatches(colorConfig: ColorConfig | null | undefined): PaletteSwatches {
  if (!colorConfig) {
    return getDefaultSwatches();
  }

  if (colorConfig.mode === 'baseColor' && colorConfig.baseColor) {
    const generated = getBasePaletteColors(colorConfig.baseColor);
    return {
      headerColors: generated,
      itemColors: generated,
    };
  }

  if (colorConfig.mode === 'palette' && colorConfig.paletteId) {
    return resolvePaletteSwatches(getStaticPalette(colorConfig.paletteId));
  }

  return getDefaultSwatches();
}

export function getPaletteHeaderColors(colorConfig: ColorConfig | null | undefined): string[] {
  return getPaletteSwatches(colorConfig).headerColors;
}

/**
 * Get palette colors from colorConfig
 * Handles both base color mode and palette mode
 * @param colorConfig - Color configuration from layout
 * @returns Array of hex color strings
 */
export function getPaletteColors(colorConfig: ColorConfig | null | undefined): string[] {
  return getPaletteSwatches(colorConfig).itemColors;
}

function getLuminance(color: string): number {
  try {
    const c = chroma(color);
    const alpha = c.alpha();
    if (alpha < 1) {
      const [r, g, b] = c.rgb();
      const blendedR = alpha * r + (1 - alpha) * 255;
      const blendedG = alpha * g + (1 - alpha) * 255;
      const blendedB = alpha * b + (1 - alpha) * 255;
      return chroma(blendedR, blendedG, blendedB).luminance();
    }
    return c.luminance();
  } catch {
    return 0;
  }
}

/**
 * Generate a mapping of field values to color indices
 * This determines which color from the palette should be used for each field value
 * @param items - Array of items to extract field values from
 * @param fieldId - The field ID to use for color mapping (e.g., 'status', 'team', or custom field ID)
 * @param orderedHeaders - Optional array of headers in display order. If provided, maps field values to their position in this array instead of alphabetical order
 * @returns Record mapping field values to color indices
 */
export function generateItemColorMap(
  items: ItemV2[],
  fieldId: string,
  orderedHeaders?: Array<{ id: string }>,
): Record<string, number> {
  // This ensures colors match the user-defined header ordering
  if (orderedHeaders && orderedHeaders.length > 0) {
    const colorMap: Record<string, number> = {};
    orderedHeaders.forEach((header, index) => {
      colorMap[header.id] = index;
    });
    return colorMap;
  }

  const uniqueValues = new Set<string>();
  let hasNoValue = false;

  items.forEach((item) => {
    const fieldValue = getItemFieldValue(item, fieldId);
    if (fieldValue !== undefined) {
      uniqueValues.add(fieldValue);
    } else {
      hasNoValue = true;
    }
  });

  // IMPORTANT: Filter out 'null' (No Value) before sorting to ensure consistent indices
  const sortedValues = Array.from(uniqueValues).sort();

  if (hasNoValue && !sortedValues.includes('null')) {
    sortedValues.push('null');
  }

  const colorMap: Record<string, number> = {};
  sortedValues.forEach((value, index) => {
    colorMap[value] = index;
  });

  return colorMap;
}

/**
 * Determine if text should be black or white based on background color
 * Returns '#000000' for light backgrounds, '#FFFFFF' for dark backgrounds
 */
export function getContrastTextColor(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor);
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

export const SUBHEADER_OPACITY = 28;
export const VALID_DROP_TARGET_OPACITY = SUBHEADER_OPACITY * 3;

/**
 * Apply opacity to a hex color and return as rgba CSS string
 */
export function applyOpacity(hexColor: string, opacity: number): string {
  return chroma(hexColor).alpha(opacity).css();
}
